import React, { useState } from 'react';
import { useEditMode } from '../contexts/EditModeContext';
import TimelineIcon, { getAvailableIcons, getAvailablePacks } from './TimelineIcons';

function EditableSchedule({ schedule, onChange }) {
  const { isEditMode } = useEditMode();
  const [expandedIcon, setExpandedIcon] = useState(null);
  const [selectedPack, setSelectedPack] = useState({});

  const addDay = () => {
    onChange([...schedule, { day: 'New Day', events: [] }]);
  };

  const removeDay = (dayIndex) => {
    onChange(schedule.filter((_, i) => i !== dayIndex));
  };

  const moveDay = (dayIndex, direction) => {
    const newSchedule = [...schedule];
    const newIndex = dayIndex + direction;
    if (newIndex < 0 || newIndex >= schedule.length) return;
    [newSchedule[dayIndex], newSchedule[newIndex]] = [newSchedule[newIndex], newSchedule[dayIndex]];
    onChange(newSchedule);
  };

  const updateDay = (dayIndex, newDay) => {
    const updated = [...schedule];
    updated[dayIndex].day = newDay;
    onChange(updated);
  };

  const addEvent = (dayIndex) => {
    const updated = [...schedule];
    updated[dayIndex].events.push({ time: '12:00 PM', event: 'New Event', icon: 'rings', pack: 'default' });
    onChange(updated);
  };

  const removeEvent = (dayIndex, eventIndex) => {
    const updated = [...schedule];
    updated[dayIndex].events = updated[dayIndex].events.filter((_, i) => i !== eventIndex);
    onChange(updated);
  };

  const updateEvent = (dayIndex, eventIndex, field, value) => {
    const updated = [...schedule];
    updated[dayIndex].events[eventIndex][field] = value;
    onChange(updated);
  };

  if (!isEditMode) {
    return (
      <div className="timeline-vertical">
        {schedule.map((day, dayIdx) => (
          <div key={dayIdx} className="timeline-day-section">
            <h3 className="timeline-day-title">{day.day}</h3>
            <div className="timeline-events">
              {day.events.map((item, idx) => (
                <div key={idx} className="timeline-event-row">
                  <div className="timeline-event-time">{item.time}</div>
                  <div className="timeline-event-line"></div>
                  <TimelineIcon name={item.icon || 'rings'} pack={item.pack || 'default'} />
                  <div className="timeline-event-name">{item.event}</div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="timeline-vertical editable-schedule">
      {schedule.map((day, dayIdx) => (
        <div key={dayIdx} className="timeline-day-section-edit">
          <div className="schedule-day-header-edit">
            <div className="schedule-day-controls">
              <button 
                onClick={() => moveDay(dayIdx, -1)} 
                className="btn-reorder"
                disabled={dayIdx === 0}
                title="Move up"
              >
                ↑
              </button>
              <button 
                onClick={() => moveDay(dayIdx, 1)} 
                className="btn-reorder"
                disabled={dayIdx === schedule.length - 1}
                title="Move down"
              >
                ↓
              </button>
            </div>
            <input
              type="text"
              value={day.day}
              onChange={(e) => updateDay(dayIdx, e.target.value)}
              className="day-input-edit"
              placeholder="Day name"
            />
            <button onClick={() => removeDay(dayIdx)} className="btn-remove">✕</button>
          </div>
          <div className="timeline-events-edit">
            {day.events.map((item, eventIdx) => {
              const eventKey = `${dayIdx}-${eventIdx}`;
              const currentPack = selectedPack[eventKey] || item.pack || 'default';
              const availableIcons = getAvailableIcons(currentPack);
              
              return (
                <div key={eventIdx} className="timeline-event-row-edit">
                  <input
                    type="text"
                    value={item.time}
                    onChange={(e) => updateEvent(dayIdx, eventIdx, 'time', e.target.value)}
                    className="timeline-event-time-edit"
                    placeholder="Time"
                  />
                  <div className="timeline-event-line-edit"></div>
                  <div className="timeline-icon-picker">
                    <button
                      className="icon-current"
                      onClick={() => setExpandedIcon(expandedIcon === eventKey ? null : eventKey)}
                      title="Click to change icon"
                    >
                      <TimelineIcon name={item.icon || 'rings'} pack={item.pack || 'default'} />
                    </button>
                    {expandedIcon === eventKey && (
                      <div className="icon-dropdown">
                        <select 
                          value={currentPack}
                          onChange={(e) => setSelectedPack({...selectedPack, [eventKey]: e.target.value})}
                          className="icon-pack-select"
                        >
                          {getAvailablePacks().map(pack => (
                            <option key={pack} value={pack}>{pack}</option>
                          ))}
                        </select>
                        <div className="icon-options">
                          {availableIcons.map(icon => (
                            <button
                              key={icon}
                              onClick={() => {
                                updateEvent(dayIdx, eventIdx, 'icon', icon);
                                updateEvent(dayIdx, eventIdx, 'pack', currentPack);
                                setExpandedIcon(null);
                              }}
                              className="icon-option"
                              title={icon}
                            >
                              <TimelineIcon name={icon} pack={currentPack} />
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                  <input
                    type="text"
                    value={item.event}
                    onChange={(e) => updateEvent(dayIdx, eventIdx, 'event', e.target.value)}
                    className="timeline-event-name-edit"
                    placeholder="Event name"
                  />
                  <button onClick={() => removeEvent(dayIdx, eventIdx)} className="btn-remove-event">✕</button>
                </div>
              );
            })}
          </div>
          <button onClick={() => addEvent(dayIdx)} className="btn-add">+ Add Event</button>
        </div>
      ))}
      <button onClick={addDay} className="btn-add-day">+ Add Day</button>
    </div>
  );
}

export default EditableSchedule;
